import { FC, useCallback, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';

interface DeleteTaskConfirmationProps {
  shouldDisplayModal?: boolean;
  toggleModal: () => void;
  confirmDeleteTask: () => void;
}

const DeleteTaskConfirmation: FC<DeleteTaskConfirmationProps> = ({
  shouldDisplayModal = false,
  toggleModal,
  confirmDeleteTask,
}) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();

  const handleCloseModal = useCallback(() => {
    toggleModal();
    closeModal();
  }, [closeModal, toggleModal]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('TASKS.TASK_DETAIL_PAGE.DELETE_TASK_MODAL.SUBMIT'),
        variant: 'primary',
        color: 'danger',
        onClick: confirmDeleteTask,
      },
      {
        children: intl.get('TASKS.TASK_DETAIL_PAGE.DELETE_TASK_MODAL.CANCEL'),
        variant: 'secondary',
        onClick: handleCloseModal,
      },
    ],
    [confirmDeleteTask, handleCloseModal]
  );

  const displayModal = useCallback(() => {
    openModal({
      title: intl.get('TASKS.TASK_DETAIL_PAGE.DELETE_TASK_MODAL.TITLE'),
      size: 'medium',
      children: (
        <>
          <Typography variant='body'>
            {intl.getHTML('TASKS.TASK_DETAIL_PAGE.DELETE_TASK_MODAL.BODY')}
          </Typography>
          <Typography variant='body' className='mt-6'>
            {intl.get('TASKS.TASK_DETAIL_PAGE.DELETE_TASK_MODAL.QUESTION')}
          </Typography>
        </>
      ),
      actionButtons,
    });
  }, [openModal, actionButtons]);

  useEffect(() => {
    if (shouldDisplayModal && !modalProps.isOpen) {
      displayModal();
    }
  }, [shouldDisplayModal, displayModal, modalProps.isOpen]);

  return (
    <Modal
      {...modalProps}
      aria-label='delete-task-confirmation-modal'
      data-cy='delete-project-modal'
      closeModal={handleCloseModal}
    />
  );
};

export default DeleteTaskConfirmation;
